/**
 * notices-reannounce.js
 *
 * Fixes a follow-on accessibility issue in the site-wide toast/notice
 * system (T4SThemeSP.Notices, used for cart stock errors, wishlist
 * confirmations, etc.) after #t4s-notices__wrapper was given
 * role="alert" aria-live="assertive" (Cart — issue #56).
 *
 * That fix makes the FIRST notice on a page announce correctly. But the
 * wrapper element is created once and reused for every later notice (only
 * its text/class gets updated, the node itself never leaves the DOM). If
 * a user triggers the exact same message twice in a row — e.g. clicking
 * "+" again on a line item already at its stock limit, which shows
 * "Not enough items available. Only 2 left." both times — most screen
 * readers only re-announce a live region when its *text* changes. Since
 * the text is byte-for-byte identical the second time, nothing appears to
 * change from the accessibility tree's point of view, so the second
 * announcement gets silently dropped.
 *
 * This file watches for the wrapper being shown (its "is--show" class
 * being (re-)added, which happens on every notice, not just the first)
 * and briefly toggles aria-live off and back on right before that. Forcing
 * the live region to disappear and reappear this way is a standard,
 * well-supported technique for making screen readers treat the next
 * content update as a fresh announcement, even when the message text is
 * unchanged.
 */
(function () {
  'use strict';

  function watchWrapper(wrapper) {
    var wasShowing = wrapper.classList.contains('is--show');

    var classObserver = new MutationObserver(function () {
      var isShowing = wrapper.classList.contains('is--show');
      if (isShowing && !wasShowing) {
        // A new notice is about to be announced. Toggle aria-live off,
        // force a reflow so the removal is actually registered, then
        // restore it — this makes the upcoming text update (even if
        // identical to last time) get announced as new.
        wrapper.removeAttribute('aria-live');
        void wrapper.offsetWidth;
        wrapper.setAttribute('aria-live', 'assertive');
      }
      wasShowing = isShowing;
    });

    classObserver.observe(wrapper, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function init() {
    var existing = document.getElementById('t4s-notices__wrapper');
    if (existing) {
      watchWrapper(existing);
      return;
    }

    // The wrapper doesn't exist until the first notice is ever triggered
    // (T4SThemeSP.Notices inserts it into the DOM on first use). Watch
    // for it to appear, then attach the real observer above.
    var insertionObserver = new MutationObserver(function () {
      var wrapper = document.getElementById('t4s-notices__wrapper');
      if (wrapper) {
        insertionObserver.disconnect();
        watchWrapper(wrapper);
      }
    });

    insertionObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
